import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// ─── Types ────────────────────────────────────────────────────────────────────
interface AreaEntry {
  area: string;
  count: number;
  inProcessCount?: number;
  appliedCount?: number;
  paidCount?: number;
  applications: {
    applicationId: string;
    email: string;
    fullName: string;
    mobileNumber: string;
    srNo: string;
    location: string;
    status: string;
    intent?: string;
    createdAt: string;
  }[];
}

// Status classification constants for area-report data:
// "In Process" = NOT payment_complete AND intent is yes/maybe/empty (Not Defined)
// "Unqualified" = NOT payment_complete AND intent is "no"
// "Successful Paid" = payment_complete

// Status counts are computed client-side from applications data via useMemo

// ─── Animations ───────────────────────────────────────────────────────────────
const waterfall = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); }`;
const barGrow = keyframes`from { width: 0; } to { width: var(--bar-width); }`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  background: linear-gradient(135deg, #ede9fe 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px 14px 0 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
  color: #fff;
  svg { width: 22px; height: 22px; }
`;

const HeaderTitle = styled.div`
  h3 { margin: 0; font-size: 1.2rem; font-weight: 700; color: #1e293b; }
  span { font-size: 0.8rem; color: #64748b; font-weight: 500; }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  transition: all 0.2s;
  &:hover { border-color: #7c3aed; color: #7c3aed; background: #faf5ff; }
  svg { width: 15px; height: 15px; }
`;

const ExportBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #059669, #047857);
  color: #fff;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.25);
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(5, 150, 105, 0.35); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: default; transform: none; }
  svg { width: 16px; height: 16px; }
`;

const SummaryBar = styled.div`
  display: flex;
  gap: 16px;
  padding: 18px 28px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div<{ $color: string; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  ${p => p.$clickable && css`
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 14px ${p.$color}30;
      border-color: ${p.$color}60;
    }
  `}
  .icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: ${p => p.$color}18;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .info { display: flex; flex-direction: column; }
  .val { font-size: 1.2rem; font-weight: 700; color: #1e293b; }
  .lbl { font-size: 0.7rem; color: #64748b; font-weight: 500; }
`;

const ViewToggle = styled.div`
  display: flex;
  margin-left: auto;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 3px;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${p => p.$active ? '#fff' : 'transparent'};
  color: ${p => p.$active ? '#7c3aed' : '#64748b'};
  box-shadow: ${p => p.$active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'};
  transition: all 0.2s;
  &:hover { color: ${p => p.$active ? '#7c3aed' : '#475569'}; }
`;

// ─── Tab Selector for "All Applications" vs "DODO NRO Status" ───────────────
const TabSelector = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 28px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  margin-left: 0;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${p => p.$active ? '#fff' : 'transparent'};
  color: ${p => p.$active ? '#7c3aed' : '#64748b'};
  border-bottom: ${p => p.$active ? '2px solid #7c3aed' : '2px solid transparent'};
  transition: all 0.2s;
  &:hover { color: #7c3aed; }
`;

// ─── DODO NRO Status View Styled Components ────────────────────────────────
const StatusStatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  padding: 20px 28px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const StatusStatCard = styled.div<{ $type: 'inProcess' | 'applied' | 'unqualified' | 'total' }>`
  background: ${p =>
    p.$type === 'inProcess'
      ? '#fef3c7'
      : p.$type === 'applied'
      ? '#dcfce7'
      : p.$type === 'unqualified'
      ? '#fee2e2'
      : '#dbeafe'};
  border-left: 4px solid ${p =>
    p.$type === 'inProcess'
      ? '#f59e0b'
      : p.$type === 'applied'
      ? '#10b981'
      : p.$type === 'unqualified'
      ? '#ef4444'
      : '#3b82f6'};
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .label {
    font-size: 12px;
    color: ${p =>
      p.$type === 'inProcess'
        ? '#92400e'
        : p.$type === 'applied'
        ? '#166534'
        : p.$type === 'unqualified'
        ? '#991b1b'
        : '#1e40af'};
    font-weight: 500;
    margin-bottom: 6px;
  }
  .count {
    font-size: 28px;
    font-weight: 700;
    color: ${p =>
      p.$type === 'inProcess'
        ? '#92400e'
        : p.$type === 'applied'
        ? '#166534'
        : p.$type === 'unqualified'
        ? '#991b1b'
        : '#1e40af'};
  }
`;

const StatusTableContainer = styled.div`
  padding: 20px 28px;
  background: #fff;
`;

const StatusTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  thead {
    background: #f1f5f9;
  }
  th {
    padding: 12px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #cbd5e1;
  }
  td {
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
  }
  tr:hover {
    background: #f8fafc;
  }
`;

const CountBadge = styled.span<{ $type: 'inProcess' | 'applied' | 'unqualified'; $clickable?: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 12px;
  background: ${p =>
    p.$type === 'inProcess' ? '#fef3c7' : p.$type === 'unqualified' ? '#fee2e2' : '#dcfce7'};
  color: ${p =>
    p.$type === 'inProcess' ? '#92400e' : p.$type === 'unqualified' ? '#991b1b' : '#166534'};
  margin: 2px;
  ${p => p.$clickable && css`
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      transform: scale(1.12);
      box-shadow: 0 2px 8px ${p.$type === 'inProcess' ? 'rgba(245,158,11,0.35)' : p.$type === 'unqualified' ? 'rgba(239,68,68,0.35)' : 'rgba(16,185,129,0.35)'};
    }
  `}
`;

// ─── Detail Modal (for clicking stat tiles / count badges) ──────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${keyframes`from{opacity:0}to{opacity:1}`} 0.2s ease;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 96%;
  max-width: 1280px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
  animation: ${keyframes`from{opacity:0;transform:translateY(24px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}`} 0.25s ease;
`;

const ModalHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: ${p => p.$color}0a;
  border-radius: 16px 16px 0 0;
`;

const ModalTitle = styled.h3<{ $color: string }>`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.15s;
  &:hover { border-color: #ef4444; color: #ef4444; background: #fef2f2; }
  svg { width: 16px; height: 16px; }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const ModalTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
  table-layout: auto;

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc;
    padding: 10px 16px;
    text-align: left;
    font-weight: 600;
    color: #475569;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
    white-space: nowrap;
  }
  td {
    padding: 10px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    white-space: nowrap;
  }
  tr:hover td { background: #f8fafc; }
`;

const ModalFooter = styled.div`
  padding: 12px 24px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
  border-radius: 0 0 16px 16px;
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const ChartSection = styled.div`
  padding: 24px 28px;
`;

const BarRow = styled.div<{ $index: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  opacity: 0;
  animation: ${waterfall} 0.4s ease forwards;
  animation-delay: ${p => Math.min(p.$index * 50, 800)}ms;
  cursor: pointer;
  border-radius: 8px;
  padding: 4px 8px;
  margin-left: -8px;
  margin-right: -8px;
  transition: background 0.15s;
  &:hover { background: #f1f5f9; }
`;

const BarLabel = styled.div<{ $isTop: boolean }>`
  width: 200px;
  min-width: 200px;
  font-size: 0.82rem;
  font-weight: ${p => p.$isTop ? 700 : 500};
  color: ${p => p.$isTop ? '#7c3aed' : '#334155'};
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 768px) { width: 120px; min-width: 120px; font-size: 0.72rem; }
`;

const BarTrack = styled.div`
  flex: 1;
  height: 32px;
  background: #f1f5f9;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const BarFill = styled.div<{ $pct: number; $rank: number }>`
  height: 100%;
  border-radius: 8px;
  --bar-width: ${p => Math.max(p.$pct, 2)}%;
  width: 0;
  animation: ${barGrow} 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${p => Math.min(p.$rank * 50 + 200, 1000)}ms;
  background: ${p => {
    if (p.$rank === 0) return 'linear-gradient(90deg, #7c3aed, #a855f7)';
    if (p.$rank === 1) return 'linear-gradient(90deg, #6366f1, #818cf8)';
    if (p.$rank === 2) return 'linear-gradient(90deg, #3b82f6, #60a5fa)';
    return 'linear-gradient(90deg, #94a3b8, #cbd5e1)';
  }};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  position: relative;
`;

const BarCount = styled.span<{ $isInside: boolean }>`
  font-size: 0.74rem;
  font-weight: 700;
  color: ${p => p.$isInside ? '#fff' : '#475569'};
  position: ${p => p.$isInside ? 'relative' : 'absolute'};
  left: ${p => p.$isInside ? 'auto' : 'calc(var(--bar-width) + 8px)'};
  top: ${p => p.$isInside ? 'auto' : '50%'};
  transform: ${p => p.$isInside ? 'none' : 'translateY(-50%)'};
  white-space: nowrap;
`;

const RankBadge = styled.span<{ $rank: number }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
  background: ${p => {
    if (p.$rank === 0) return 'linear-gradient(135deg, #f59e0b, #d97706)';
    if (p.$rank === 1) return 'linear-gradient(135deg, #94a3b8, #64748b)';
    if (p.$rank === 2) return 'linear-gradient(135deg, #cd7f32, #b8650a)';
    return '#e2e8f0';
  }};
  color: ${p => p.$rank < 3 ? '#fff' : '#64748b'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
`;

const Th = styled.th`
  padding: 13px 18px;
  font-size: 0.73rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Tr = styled.tr<{ $index: number }>`
  opacity: 0;
  animation: ${waterfall} 0.4s ease forwards;
  animation-delay: ${p => Math.min(p.$index * 40, 600)}ms;
  transition: background 0.15s;
  &:hover { background: #f1f5f9; }
`;

const Td = styled.td`
  padding: 12px 18px;
  font-size: 0.84rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
`;

const TopBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
  margin-left: 8px;
`;

const AreaCountBadge = styled.span<{ $rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${p => {
    if (p.$rank === 0) return 'linear-gradient(135deg, #ede9fe, #ddd6fe)';
    if (p.$rank === 1) return '#e0e7ff';
    if (p.$rank === 2) return '#dbeafe';
    return '#f1f5f9';
  }};
  color: ${p => {
    if (p.$rank === 0) return '#6d28d9';
    if (p.$rank === 1) return '#4338ca';
    if (p.$rank === 2) return '#1d4ed8';
    return '#475569';
  }};
`;

const ExpandBtn = styled.button<{ $expanded: boolean }>`
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background: ${p => p.$expanded ? '#ede9fe' : '#fff'};
  color: ${p => p.$expanded ? '#7c3aed' : '#64748b'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
  svg { width: 12px; height: 12px; transition: transform 0.2s; transform: rotate(${p => p.$expanded ? '180deg' : '0'}); }
`;

const SubRow = styled.tr<{ $index: number }>`
  opacity: 0;
  animation: ${waterfall} 0.3s ease forwards;
  animation-delay: ${p => Math.min(p.$index * 30, 300)}ms;
  background: #fafbfd;
  &:hover { background: #f0f1f5; }
`;

const SubTd = styled.td`
  padding: 8px 18px 8px 44px;
  font-size: 0.76rem;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  ${p => {
    const s = (p.$status || '').toLowerCase();
    if (s === 'payment_complete' || s === 'paid') return css`background: #dcfce7; color: #15803d;`;
    if (s === 'approved') return css`background: #d1fae5; color: #047857;`;
    if (s === 'draft') return css`background: #fef3c7; color: #b45309;`;
    if (s === 'in-review' || s === 'under_review') return css`background: #e0e7ff; color: #4338ca;`;
    if (s === 'rejected') return css`background: #fee2e2; color: #b91c1c;`;
    return css`background: #f1f5f9; color: #475569;`;
  }}
`;

const EmptyState = styled.div`
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
  .icon { margin-bottom: 16px; opacity: 0.5; }
  p { margin: 0; }
`;

const LoadingContainer = styled.div`
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  .dot {
    display: inline-block;
    width: 10px; height: 10px;
    border-radius: 50%;
    background: #7c3aed;
    margin: 0 4px;
    animation: ${keyframes`0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)}`} 1.2s infinite ease-in-out both;
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 28px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
  border-radius: 0 0 14px 14px;
  flex-wrap: wrap;
  gap: 10px;
`;

const FooterInfo = styled.span`
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
`;

// ─── Component ────────────────────────────────────────────────────────────────
const AreaWiseReportPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [areaReport, setAreaReport] = useState<AreaEntry[]>([]);
  const [qualifiedReport, setQualifiedReport] = useState<AreaEntry[]>([]);
  const [totalWithSrNo, setTotalWithSrNo] = useState(0);
  const [totalWithAppId, setTotalWithAppId] = useState(0);
  const [totalQualified, setTotalQualified] = useState(0);
  const [srNoReport, setSrNoReport] = useState<{ srNo: string; area: string; locality: string; count: number; inProcess: number; paid: number; applications: any[] }[]>([]);
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [expandedSrNos, setExpandedSrNos] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'all' | 'dodo-nro-status' | 'srno-wise'>('dodo-nro-status');
  const [modalFilter, setModalFilter] = useState<{ type: 'inProcess' | 'paid' | 'total' | 'area-all' | 'srno-all' | 'no-srno'; area?: string } | null>(null);
  const IN_PROCESS_STATUSES = ['pending', 'in-review', 'approved'];
  const APPLIED_STATUSES = ['submitted', 'payment_complete'];

  const fetchAreaReport = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('subAdminToken');
      const res = await axios.get(`${API_BASE}/api/admin/applications/area-report`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      if (res.data.success) {
        setAreaReport(res.data.areaReport);
        setTotalWithSrNo(res.data.totalWithSrNo);
        setTotalWithAppId(res.data.totalWithAppId || 0);
        setQualifiedReport(res.data.qualifiedReport || []);
        setTotalQualified(res.data.totalQualified || 0);
        setSrNoReport(res.data.srNoReport || []);
      }
    } catch (err) {
      console.error('Failed to fetch area report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAreaReport(); }, [fetchAreaReport]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalFilter) setModalFilter(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [modalFilter]);

  // Compute status counts from qualifiedReport data (all qualified apps — with any progress)
  // "In Process" = NOT payment_complete AND intent is yes/maybe/empty/undefined (Not Defined)
  // "Unqualified" = NOT payment_complete AND intent is "no" — should be 0 since server already filters
  // "Successful Paid" = payment_complete
  // Note: server already pre-filters qualifiedReport to exclude intent="no" for non-paid apps,
  // but we keep client-side classification for robustness
  const statusReport = useMemo(() => {
    if (qualifiedReport.length === 0) return { areas: [], summary: { totalInProcess: 0, totalPaid: 0, totalAll: 0 } };

    let totalInProcess = 0;
    let totalPaid = 0;

    const areas = qualifiedReport.map(entry => {
      let inProcess = 0;
      let paid = 0;

      for (const app of entry.applications) {
        const s = (app.status || 'draft').toLowerCase();
        if (s === 'payment_complete') {
          paid++;
        } else {
          inProcess++;
        }
      }

      totalInProcess += inProcess;
      totalPaid += paid;

      return {
        area: entry.area,
        count: entry.count,
        inProcessCount: inProcess,
        paidCount: paid,
      };
    });

    return {
      areas,
      summary: {
        totalInProcess,
        totalPaid,
        totalAll: totalInProcess + totalPaid,
      },
    };
  }, [qualifiedReport]);

  // Compute qualified apps without Sr.No (from qualifiedReport's "No Area Selected" bucket)
  const qualifiedWithoutSrNo = useMemo(() => {
    const noAreaEntry = qualifiedReport.find(e => e.area === 'No Area Selected');
    return noAreaEntry ? noAreaEntry.count : 0;
  }, [qualifiedReport]);

  // Build the filtered application list for the modal
  const modalApplications = useMemo(() => {
    if (!modalFilter) return [];

    // "srno-all" shows all applications with Sr.No (from areaReport)
    if (modalFilter.type === 'srno-all') {
      const results: any[] = [];
      for (const entry of areaReport) {
        for (const app of entry.applications) {
          results.push({ area: entry.area, ...app });
        }
      }
      return results;
    }

    // "no-srno" shows qualified apps WITHOUT Sr.No (from qualifiedReport's "No Area Selected")
    if (modalFilter.type === 'no-srno') {
      const results: any[] = [];
      for (const entry of qualifiedReport) {
        if (entry.area === 'No Area Selected') {
          for (const app of entry.applications) {
            results.push({ area: 'No Area Selected', ...app });
          }
        }
      }
      return results;
    }

    // "area-all" uses areaReport (Sr.No-based area view)
    if (modalFilter.type === 'area-all') {
      const results: any[] = [];
      for (const entry of areaReport) {
        if (modalFilter.area && entry.area !== modalFilter.area) continue;
        for (const app of entry.applications) {
          results.push({ area: entry.area, ...app });
        }
      }
      return results;
    }

    // DODO NRO filters use qualifiedReport (broader: any progress + intent != no)
    const results: any[] = [];
    for (const entry of qualifiedReport) {
      if (modalFilter.area && entry.area !== modalFilter.area) continue;
      for (const app of entry.applications) {
        const s = (app.status || 'draft').toLowerCase();

        if (modalFilter.type === 'total') {
          results.push({ area: entry.area, ...app });
        } else if (modalFilter.type === 'inProcess' && s !== 'payment_complete') {
          results.push({ area: entry.area, ...app });
        } else if (modalFilter.type === 'paid' && s === 'payment_complete') {
          results.push({ area: entry.area, ...app });
        }
      }
    }
    return results;
  }, [modalFilter, areaReport, qualifiedReport]);

  const modalMeta = useMemo(() => {
    if (!modalFilter) return { title: '', color: '', emoji: '', showIntent: false };
    const areaLabel = modalFilter.area ? ` — ${modalFilter.area}` : '';
    if (modalFilter.type === 'area-all') return { title: `All Applications${areaLabel}`, color: '#7c3aed', emoji: '📍', showIntent: true };
    if (modalFilter.type === 'srno-all') return { title: `All Applications with Sr.No`, color: '#3b82f6', emoji: '📋', showIntent: true };
    if (modalFilter.type === 'no-srno') return { title: `Qualified Applications without Sr.No`, color: '#ef4444', emoji: '📭', showIntent: true };
    if (modalFilter.type === 'inProcess') return { title: `In Process — Qualified Applications${areaLabel}`, color: '#b45309', emoji: '⏳', showIntent: true };
    if (modalFilter.type === 'paid') return { title: `Successful Paid Applications${areaLabel}`, color: '#047857', emoji: '✅', showIntent: true };
    return { title: `All DODO NRO Applications${areaLabel}`, color: '#1e40af', emoji: '📊', showIntent: true };
  }, [modalFilter]);

  const toggleExpand = (area: string) => {
    setExpandedAreas(prev => {
      const next = new Set(prev);
      if (next.has(area)) next.delete(area);
      else next.add(area);
      return next;
    });
  };

  const exportToXlsx = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'MNGLDODO Admin Dashboard';
    wb.created = new Date();
    const now = new Date();
    const todayFull = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
    // IST date for filename: "23rdMarch2026"
    const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const dd = istNow.getDate();
    const suffix = dd === 1 || dd === 21 || dd === 31 ? 'st' : dd === 2 || dd === 22 ? 'nd' : dd === 3 || dd === 23 ? 'rd' : 'th';
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const fileDate = `${dd}${suffix}${monthNames[istNow.getMonth()]}${istNow.getFullYear()}`;

    // ─── Color palette ───
    const C = {
      brand: 'FF7C3AED', brandLight: 'FFEDE9FE',
      green: 'FF047857', greenBg: 'FFF0FDF4', greenLight: 'FFDCFCE7',
      amber: 'FFB45309', amberBg: 'FFFFFBEB', amberLight: 'FFFEF3C7',
      blue: 'FF1E40AF', blueBg: 'FFEFF6FF', blueLight: 'FFDBEAFE',
      red: 'FFDC2626', redBg: 'FFFEF2F2',
      gray: 'FF64748B', grayBg: 'FFF8FAFC', grayBorder: 'FFCBD5E1',
      dark: 'FF1E293B', white: 'FFFFFFFF',
    };
    const border = (color = C.grayBorder): Partial<ExcelJS.Borders> => ({
      top: { style: 'thin', color: { argb: color } },
      bottom: { style: 'thin', color: { argb: color } },
      left: { style: 'thin', color: { argb: color } },
      right: { style: 'thin', color: { argb: color } },
    });
    const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: C.white }, size: 11, name: 'Calibri' };
    const headerFill = (color: string): ExcelJS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: color } });
    const bodyFont: Partial<ExcelJS.Font> = { size: 10, name: 'Calibri', color: { argb: C.dark } };

    const styleDataSheet = (ws: ExcelJS.Worksheet, headerColor: string, cols: Partial<ExcelJS.Column>[]) => {
      ws.columns = cols;
      const hRow = ws.getRow(1);
      hRow.font = headerFont;
      hRow.fill = headerFill(headerColor);
      hRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      hRow.height = 28;
      hRow.eachCell(c => { c.border = border(); });
      for (let r = 2; r <= ws.rowCount; r++) {
        const row = ws.getRow(r);
        row.font = bodyFont;
        row.alignment = { vertical: 'middle', wrapText: false };
        row.height = 22;
        const isAlt = r % 2 === 0;
        row.eachCell(c => {
          c.border = border();
          if (isAlt) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.grayBg } };
        });
      }
      ws.autoFilter = { from: 'A1', to: `${String.fromCharCode(64 + cols.length)}1` };
      ws.views = [{ state: 'frozen', ySplit: 1 }];
    };

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 0: How to Read This Report (Instructions)
    // ══════════════════════════════════════════════════════════════════════════════
    const wsGuide = wb.addWorksheet('How to Read This Report', { properties: { tabColor: { argb: C.blue } } });
    wsGuide.columns = [{ width: 4 }, { width: 80 }];

    wsGuide.mergeCells('A1:B1');
    const guideTitle = wsGuide.getCell('A1');
    guideTitle.value = '📖 How to Read This Report';
    guideTitle.font = { bold: true, size: 18, color: { argb: C.brand }, name: 'Calibri' };
    guideTitle.alignment = { vertical: 'middle' };
    wsGuide.getRow(1).height = 36;

    wsGuide.mergeCells('A2:B2');
    wsGuide.getCell('A2').value = `Generated: ${todayFull}`;
    wsGuide.getCell('A2').font = { italic: true, size: 10, color: { argb: C.gray }, name: 'Calibri' };
    wsGuide.getRow(2).height = 20;

    const guideContent: [string, string][] = [
      ['Sheet', 'What It Contains'],
      ['Executive Summary', 'High-level metrics — total qualified, in process, paid — with descriptions. Start here for a quick overview.'],
      ['Sr.No Wise Breakdown', 'Each CNG Station Sr.No with the count of applications, how many are In Process vs Paid. Use this to answer "how many applicants per location?".'],
      ['In Process Applications', 'Full list of applicants who have started but NOT yet paid. Includes name, mobile, email, area, Sr.No, intent, and application date.'],
      ['Successful Paid', 'Full list of applicants who have completed payment. Same columns as In Process sheet.'],
      ['Sr.No Wise Applicants', 'Detailed per-applicant view grouped by Sr.No — shows each Sr.No and the applicants under it with their status.'],
    ];

    let gr = 4;
    // Section header
    wsGuide.mergeCells(`A${gr}:B${gr}`);
    const secH = wsGuide.getCell(`A${gr}`);
    secH.value = 'SHEET-BY-SHEET GUIDE';
    secH.font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
    secH.fill = headerFill(C.brand);
    secH.alignment = { vertical: 'middle' };
    secH.border = border();
    wsGuide.getRow(gr).height = 28;
    gr++;

    // Table header
    wsGuide.getCell(gr, 1).value = '#';
    wsGuide.getCell(gr, 2).value = guideContent[0][1];
    [1, 2].forEach(i => {
      const c = wsGuide.getCell(gr, i);
      c.font = { bold: true, size: 10, color: { argb: C.dark }, name: 'Calibri' };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.brandLight } };
      c.border = border();
      c.alignment = { vertical: 'middle' };
    });
    wsGuide.getCell(gr, 1).value = 'Sheet Name';
    wsGuide.getRow(gr).height = 24;
    gr++;

    for (let si = 1; si < guideContent.length; si++) {
      wsGuide.getCell(gr, 1).value = guideContent[si][0];
      wsGuide.getCell(gr, 1).font = { bold: true, size: 10, name: 'Calibri', color: { argb: C.brand } };
      wsGuide.getCell(gr, 2).value = guideContent[si][1];
      wsGuide.getCell(gr, 2).font = { size: 10, name: 'Calibri', color: { argb: C.dark } };
      wsGuide.getCell(gr, 2).alignment = { wrapText: true, vertical: 'middle' };
      [1, 2].forEach(i => {
        wsGuide.getCell(gr, i).border = border();
        if (si % 2 === 0) wsGuide.getCell(gr, i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.grayBg } };
      });
      wsGuide.getRow(gr).height = 36;
      gr++;
    }

    gr += 2;
    wsGuide.mergeCells(`A${gr}:B${gr}`);
    const tipH = wsGuide.getCell(`A${gr}`);
    tipH.value = 'QUICK TIPS';
    tipH.font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
    tipH.fill = headerFill(C.green);
    tipH.alignment = { vertical: 'middle' };
    tipH.border = border();
    wsGuide.getRow(gr).height = 28;
    gr++;

    const tips = [
      '✅ Use filters (dropdown arrows on headers) to narrow down data in any sheet.',
      '✅ "In Process" means the applicant has started but NOT paid yet — they are qualified (Intent: Yes/Maybe/Not Defined) and have made progress.',
      '✅ "Successful Paid" means the applicant has completed payment.',
      '✅ The "Sr.No Wise Breakdown" sheet is the best way to see demand per CNG station location.',
      '✅ All sheets have frozen headers — scroll down and the header row stays visible.',
    ];
    for (const tip of tips) {
      wsGuide.mergeCells(`A${gr}:B${gr}`);
      wsGuide.getCell(`A${gr}`).value = tip;
      wsGuide.getCell(`A${gr}`).font = { size: 10, name: 'Calibri', color: { argb: C.dark } };
      wsGuide.getCell(`A${gr}`).alignment = { wrapText: true, vertical: 'middle' };
      wsGuide.getCell(`A${gr}`).border = border();
      wsGuide.getRow(gr).height = 26;
      gr++;
    }

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 1: Executive Summary
    // ══════════════════════════════════════════════════════════════════════════════
    const ws1 = wb.addWorksheet('Executive Summary', { properties: { tabColor: { argb: C.brand } } });
    ws1.columns = [{ width: 36 }, { width: 14 }, { width: 75 }];

    // Title
    ws1.mergeCells('A1:C1');
    const titleCell = ws1.getCell('A1');
    titleCell.value = 'DODO NRO — Complete Application Report';
    titleCell.font = { bold: true, size: 18, color: { argb: C.brand }, name: 'Calibri' };
    titleCell.alignment = { vertical: 'middle' };
    ws1.getRow(1).height = 36;

    ws1.mergeCells('A2:C2');
    const dateCell = ws1.getCell('A2');
    dateCell.value = `Report Generated: ${todayFull}`;
    dateCell.font = { size: 11, italic: true, color: { argb: C.gray }, name: 'Calibri' };
    ws1.getRow(2).height = 22;

    // ── Overall Status section ──
    let row = 4;
    ws1.mergeCells(`A${row}:C${row}`);
    const secTitle1 = ws1.getCell(`A${row}`);
    secTitle1.value = 'OVERALL STATUS';
    secTitle1.font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
    secTitle1.fill = headerFill(C.brand);
    secTitle1.alignment = { vertical: 'middle' };
    secTitle1.border = border();
    ws1.getRow(row).height = 28;

    row++;
    ['Metric', 'Count', 'Description'].forEach((h, i) => {
      const c = ws1.getCell(row, i + 1);
      c.value = h; c.font = { bold: true, size: 10, color: { argb: C.dark }, name: 'Calibri' };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.brandLight } };
      c.border = border(); c.alignment = { vertical: 'middle' };
    });
    ws1.getRow(row).height = 24;

    const statusData = [
      ['In Process (Qualified)', statusReport.summary.totalInProcess, 'Applicants with Intent Yes/Maybe/Not Defined who have started their application and made progress — uploaded documents, entered personal details, uploaded profile picture, or selected a CNG Station Location.', C.amberBg],
      ['Successful Paid', statusReport.summary.totalPaid, 'Applicants who have completed payment and fully submitted their DODO NRO application.', C.greenBg],
      ['Total Qualified', statusReport.summary.totalAll, `Sum of In Process (${statusReport.summary.totalInProcess}) + Successful Paid (${statusReport.summary.totalPaid}).`, C.blueBg],
    ];
    for (const [label, count, desc, bg] of statusData) {
      row++;
      ws1.getCell(row, 1).value = label as string;
      ws1.getCell(row, 1).font = { bold: true, size: 11, name: 'Calibri', color: { argb: C.dark } };
      ws1.getCell(row, 2).value = count as number;
      ws1.getCell(row, 2).font = { bold: true, size: 14, name: 'Calibri', color: { argb: C.brand } };
      ws1.getCell(row, 2).alignment = { horizontal: 'center', vertical: 'middle' };
      ws1.getCell(row, 3).value = desc as string;
      ws1.getCell(row, 3).font = { size: 10, name: 'Calibri', color: { argb: C.gray } };
      ws1.getCell(row, 3).alignment = { wrapText: true, vertical: 'middle' };
      for (let i = 1; i <= 3; i++) {
        ws1.getCell(row, i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg as string } };
        ws1.getCell(row, i).border = border();
      }
      ws1.getRow(row).height = 32;
    }

    // ── Sr.No Stats ──
    row += 2;
    ws1.getCell(row, 1).value = 'With Sr.No Selected';
    ws1.getCell(row, 1).font = { bold: true, size: 11, name: 'Calibri' };
    ws1.getCell(row, 2).value = totalWithSrNo;
    ws1.getCell(row, 2).font = { bold: true, size: 13, name: 'Calibri', color: { argb: C.blue } };
    ws1.getCell(row, 2).alignment = { horizontal: 'center' };
    ws1.getCell(row, 3).value = 'Applications that have selected a specific CNG Station Location Sr.No (Intent: Yes/Maybe/Not Defined only).';
    ws1.getCell(row, 3).font = { size: 10, name: 'Calibri', color: { argb: C.gray } };
    ws1.getCell(row, 3).alignment = { wrapText: true };
    for (let i = 1; i <= 3; i++) { ws1.getCell(row, i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.blueLight } }; ws1.getCell(row, i).border = border(); }
    ws1.getRow(row).height = 28;

    row++;
    ws1.getCell(row, 1).value = 'Without Sr.No (Qualified)';
    ws1.getCell(row, 1).font = { bold: true, size: 11, name: 'Calibri' };
    ws1.getCell(row, 2).value = qualifiedWithoutSrNo;
    ws1.getCell(row, 2).font = { bold: true, size: 13, name: 'Calibri', color: { argb: C.red } };
    ws1.getCell(row, 2).alignment = { horizontal: 'center' };
    ws1.getCell(row, 3).value = 'Qualified applicants who have started filling the form but have NOT yet selected a CNG Station Location Sr.No.';
    ws1.getCell(row, 3).font = { size: 10, name: 'Calibri', color: { argb: C.gray } };
    ws1.getCell(row, 3).alignment = { wrapText: true };
    for (let i = 1; i <= 3; i++) { ws1.getCell(row, i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.redBg } }; ws1.getCell(row, i).border = border(); }
    ws1.getRow(row).height = 28;

    // ── Location-Wise Summary ──
    row += 2;
    ws1.mergeCells(`A${row}:C${row}`);
    ws1.getCell(`A${row}`).value = 'LOCATION-WISE SUMMARY';
    ws1.getCell(`A${row}`).font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
    ws1.getCell(`A${row}`).fill = headerFill(C.green);
    ws1.getCell(`A${row}`).alignment = { vertical: 'middle' };
    ws1.getCell(`A${row}`).border = border();
    ws1.getRow(row).height = 28;

    row++;
    ['Location / Area', 'In Process', 'Paid'].forEach((h, i) => {
      const c = ws1.getCell(row, i + 1);
      c.value = h; c.font = { bold: true, size: 10, color: { argb: C.dark }, name: 'Calibri' };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.greenLight } };
      c.border = border(); c.alignment = { vertical: 'middle', horizontal: i > 0 ? 'center' : 'left' };
    });
    // Add Total column header in col D
    ws1.getColumn(4).width = 10;
    const totalHdr = ws1.getCell(row, 4);
    totalHdr.value = 'Total'; totalHdr.font = { bold: true, size: 10, color: { argb: C.dark }, name: 'Calibri' };
    totalHdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.greenLight } };
    totalHdr.border = border(); totalHdr.alignment = { horizontal: 'center', vertical: 'middle' };
    ws1.getRow(row).height = 24;

    for (const a of statusReport.areas) {
      row++;
      ws1.getCell(row, 1).value = a.area;
      ws1.getCell(row, 1).font = { bold: true, size: 10, name: 'Calibri' };
      ws1.getCell(row, 2).value = a.inProcessCount;
      ws1.getCell(row, 2).alignment = { horizontal: 'center' };
      ws1.getCell(row, 3).value = a.paidCount;
      ws1.getCell(row, 3).alignment = { horizontal: 'center' };
      ws1.getCell(row, 4).value = a.count;
      ws1.getCell(row, 4).font = { bold: true, size: 10, name: 'Calibri' };
      ws1.getCell(row, 4).alignment = { horizontal: 'center' };
      for (let i = 1; i <= 4; i++) {
        ws1.getCell(row, i).border = border();
        ws1.getCell(row, i).font = { ...ws1.getCell(row, i).font as any, name: 'Calibri' };
      }
      ws1.getRow(row).height = 22;
    }

    // ── Sr.No Statistics ──
    row += 2;
    ws1.mergeCells(`A${row}:C${row}`);
    ws1.getCell(`A${row}`).value = 'SR.NO STATISTICS';
    ws1.getCell(`A${row}`).font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
    ws1.getCell(`A${row}`).fill = headerFill(C.amber);
    ws1.getCell(`A${row}`).alignment = { vertical: 'middle' };
    ws1.getCell(`A${row}`).border = border();
    ws1.getRow(row).height = 28;

    const srStats = [
      ['Unique Sr.No Locations', srNoReport.length, 'Distinct CNG station locations where at least one qualified applicant has applied.'],
      ['Locations with Multiple Apps', srNoReport.filter(s => s.count > 1).length, 'High-demand locations where more than one applicant has applied for the same Sr.No.'],
    ];
    for (const [label, count, desc] of srStats) {
      row++;
      ws1.getCell(row, 1).value = label as string;
      ws1.getCell(row, 1).font = { bold: true, size: 10, name: 'Calibri' };
      ws1.getCell(row, 2).value = count as number;
      ws1.getCell(row, 2).font = { bold: true, size: 12, name: 'Calibri', color: { argb: C.amber } };
      ws1.getCell(row, 2).alignment = { horizontal: 'center' };
      ws1.getCell(row, 3).value = desc as string;
      ws1.getCell(row, 3).font = { size: 10, name: 'Calibri', color: { argb: C.gray } };
      ws1.getCell(row, 3).alignment = { wrapText: true };
      for (let i = 1; i <= 3; i++) {
        ws1.getCell(row, i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.amberLight } };
        ws1.getCell(row, i).border = border();
      }
      ws1.getRow(row).height = 28;
    }

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 2: Sr.No Wise Breakdown
    // ══════════════════════════════════════════════════════════════════════════════
    const ws2 = wb.addWorksheet('SrNo Wise Breakdown', { properties: { tabColor: { argb: C.amber } } });
    ws2.addRow(['#', 'Sr.No', 'Area', 'Locality / Stretch', 'In Process', 'Paid', 'Total']);
    srNoReport.forEach((s, i) => {
      const r = ws2.addRow([i + 1, Number(s.srNo) || s.srNo, s.area, s.locality || '-', s.inProcess, s.paid, s.count]);
      if (s.count > 1) {
        r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.amberBg } }; });
        r.getCell(7).font = { bold: true, size: 12, name: 'Calibri', color: { argb: C.red } };
      }
    });
    styleDataSheet(ws2, C.amber, [
      { width: 5, key: 'a' }, { width: 8, key: 'b' }, { width: 10, key: 'c' },
      { width: 55, key: 'd' }, { width: 13, key: 'e' }, { width: 8, key: 'f' }, { width: 10, key: 'g' },
    ]);

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 3: Successful Paid Applications (moved before In Process)
    // ══════════════════════════════════════════════════════════════════════════════
    const ws3 = wb.addWorksheet('Successful Paid', { properties: { tabColor: { argb: C.green } } });
    ws3.addRow([
      'Application ID', 'Applicant Name', 'Sr.No', 'Location', 'CA', 'Area',
      'Stretch Side(LHS/RHS)', 'Email', 'Mobile', 'Application Type',
      'Offered Land/Plot Ownership Status', 'Period of Land Lease to be given to MNGL',
      'Complete Offered Land/Plot Address', 'Offered Land/Plot Location',
      'Frontage (in Mtrs) of Offered Land/Plot to MNGL',
      'Depth (in Mtrs) of Offered Land/Plot to MNGL',
      'Total Area (Sq. Mtrs) of offered Land/Plot to MNGL',
      'Applied On',
    ]);
    for (const entry of qualifiedReport) {
      for (const app of (entry as any).applications) {
        if ((app.status || '').toLowerCase() !== 'payment_complete') continue;
        ws3.addRow([
          app.applicationId,
          app.fullName || '-',
          app.srNo || '-',
          app.location || '-',
          app.ca || '-',
          entry.area,
          app.stretchSide || '-',
          app.email || '-',
          app.mobileNumber || '-',
          app.applicationType || '-',
          app.landOwnership || '-',
          app.leasePeriod || '-',
          app.landAddress || '-',
          app.landLocation || '-',
          app.frontage || '-',
          app.depth || '-',
          app.totalArea || '-',
          fmtDate(app.createdAt),
        ]);
      }
    }
    styleDataSheet(ws3, C.green, [
      { width: 30, key: 'a' }, { width: 28, key: 'b' }, { width: 8, key: 'c' }, { width: 20, key: 'd' },
      { width: 12, key: 'e' }, { width: 18, key: 'f' }, { width: 18, key: 'g' }, { width: 30, key: 'h' },
      { width: 15, key: 'i' }, { width: 18, key: 'j' }, { width: 28, key: 'k' }, { width: 22, key: 'l' },
      { width: 40, key: 'm' }, { width: 25, key: 'n' }, { width: 18, key: 'o' }, { width: 18, key: 'p' },
      { width: 20, key: 'q' }, { width: 14, key: 'r' },
    ]);

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 4: In Process Applications
    // ══════════════════════════════════════════════════════════════════════════════
    const ws4 = wb.addWorksheet('In Process Applications', { properties: { tabColor: { argb: 'FFF59E0B' } } });
    ws4.addRow(['#', 'Area', 'Sr.No', 'Application ID', 'Applicant Name', 'Email', 'Mobile', 'Intent', 'Status', 'Applied On']);
    let inIdx = 0;
    for (const entry of qualifiedReport) {
      for (const app of entry.applications) {
        if ((app.status || '').toLowerCase() === 'payment_complete') continue;
        inIdx++;
        const r = ws4.addRow([inIdx, entry.area, app.srNo || '-', app.applicationId, app.fullName || '-', app.email || '-', app.mobileNumber || '-', app.intent || '—', app.status || 'draft', fmtDate(app.createdAt)]);
        // Color-code intent
        const intentCell = r.getCell(8);
        const intent = (app.intent || '').toLowerCase();
        if (intent === 'yes') { intentCell.font = { ...bodyFont, bold: true, color: { argb: C.green } }; intentCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.greenBg } }; }
        else if (intent === 'maybe') { intentCell.font = { ...bodyFont, bold: true, color: { argb: C.amber } }; intentCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.amberBg } }; }
      }
    }
    styleDataSheet(ws4, 'FFF59E0B', [
      { width: 5, key: 'a' }, { width: 18, key: 'b' }, { width: 8, key: 'c' }, { width: 30, key: 'd' },
      { width: 28, key: 'e' }, { width: 30, key: 'f' }, { width: 15, key: 'g' }, { width: 10, key: 'h' },
      { width: 18, key: 'i' }, { width: 14, key: 'j' },
    ]);

    // ══════════════════════════════════════════════════════════════════════════════
    // SHEET 5: Sr.No Wise Applicants (detailed)
    // ══════════════════════════════════════════════════════════════════════════════
    const ws5 = wb.addWorksheet('SrNo Wise Applicants', { properties: { tabColor: { argb: C.blue } } });
    ws5.addRow(['Sr.No', 'Area', 'Locality', 'Application ID', 'Applicant Name', 'Email', 'Mobile', 'Intent', 'Status', 'Applied On']);
    for (const s of srNoReport) {
      for (const app of s.applications) {
        const r = ws5.addRow([Number(s.srNo) || s.srNo, s.area, s.locality || '-', app.applicationId, app.fullName || '-', app.email || '-', app.mobileNumber || '-', app.intent || '—', app.status === 'payment_complete' ? 'Paid' : 'In Process', fmtDate(app.createdAt)]);
        if (app.status === 'payment_complete') {
          r.getCell(9).font = { ...bodyFont, bold: true, color: { argb: C.green } };
          r.getCell(9).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.greenBg } };
        }
      }
    }
    styleDataSheet(ws5, C.blue, [
      { width: 8, key: 'a' }, { width: 10, key: 'b' }, { width: 50, key: 'c' }, { width: 30, key: 'd' },
      { width: 28, key: 'e' }, { width: 30, key: 'f' }, { width: 15, key: 'g' }, { width: 10, key: 'h' },
      { width: 12, key: 'i' }, { width: 14, key: 'j' },
    ]);

    // ─── Download ───
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DODO_NRO_Complete_Report_${fileDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export modal data to XLSX
  const exportModalToXlsx = useCallback(() => {
    if (modalApplications.length === 0) return;
    const rows = modalApplications.map((app, idx) => ({
      '#': idx + 1,
      'Area': app.area || '-',
      'Sr.No': app.srNo || '-',
      'Application ID': app.applicationId,
      'Applicant Name': app.fullName || '-',
      'Email': (app as any).email || '-',
      'Mobile': app.mobileNumber || '-',
      'Intent': (app as any).intent || '—',
      'Status': app.status,
      'Applied On': app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = [{ wch: 4 }, { wch: 18 }, { wch: 8 }, { wch: 28 }, { wch: 24 }, { wch: 28 }, { wch: 14 }, { wch: 8 }, { wch: 18 }, { wch: 14 }];
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    const sheetName = (modalMeta.title || 'Applications').replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = `${sheetName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [modalApplications, modalMeta]);

  const maxCount = areaReport.length > 0 ? areaReport[0].count : 1;

  return (
    <PageContainer>
      <HeaderBar>
        <HeaderLeft>
          <IconBadge>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </IconBadge>
          <HeaderTitle>
            <h3>Area Wise Report</h3>
            <span>Application distribution by CNG Station Location area</span>
          </HeaderTitle>
        </HeaderLeft>
        <HeaderActions>
          <RefreshBtn onClick={fetchAreaReport} title="Refresh">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </RefreshBtn>
          <ExportBtn onClick={exportToXlsx} disabled={areaReport.length === 0} title="Export to XLSX">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export XLSX
          </ExportBtn>
        </HeaderActions>
      </HeaderBar>

      {loading ? (
        <LoadingContainer>
          <div><span className="dot" /><span className="dot" /><span className="dot" /></div>
          <p style={{ marginTop: 16, fontSize: '0.85rem' }}>Loading area report...</p>
        </LoadingContainer>
      ) : areaReport.length === 0 ? (
        <EmptyState>
          <div className="icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} width="48" height="48">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p>No applications with Sr.No filled in yet</p>
        </EmptyState>
      ) : (
        <>
          <SummaryBar>
            <SummaryCard $color="#7c3aed" title={`Total number of distinct CNG Station Location areas (e.g. PCMC, PMC) from applications that have Sr.No selected`}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#7c3aed" strokeWidth={2} width="17" height="17">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="info">
                <span className="val">{areaReport.length}</span>
                <span className="lbl">Total Areas</span>
              </div>
            </SummaryCard>
            <SummaryCard $color="#ef4444" $clickable onClick={() => setModalFilter({ type: 'no-srno' })} title={`Qualified applications (Intent: Yes/Maybe/— with progress) that have NOT selected a CNG Station Location Sr.No yet.\nClick to view these applications.`}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2} width="17" height="17">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="info">
                <span className="val">{qualifiedWithoutSrNo}</span>
                <span className="lbl">Without Sr.No</span>
              </div>
            </SummaryCard>
            <SummaryCard $color="#3b82f6" $clickable onClick={() => setModalFilter({ type: 'srno-all' })} title={`Applications with CNG Station Location Sr.No selected\nand Intent: Yes/Maybe/Not Defined (excludes Intent "No").\nClick to view these applications.`}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#3b82f6" strokeWidth={2} width="17" height="17">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="info">
                <span className="val">{totalWithSrNo}</span>
                <span className="lbl">With Sr.No</span>
              </div>
            </SummaryCard>
            {areaReport.map((entry, i) => {
              const areaColors = ['#f59e0b', '#10b981', '#6366f1', '#8b5cf6', '#ec4899'];
              const color = areaColors[i % areaColors.length];
              return (
                <SummaryCard key={entry.area} $color={color} $clickable onClick={() => setModalFilter({ type: 'area-all', area: entry.area })} title={`${entry.count} applications in ${entry.area} area\nwith Sr.No selected and Intent: Yes/Maybe/— (excludes "No").\nClick to view all applications in this area.`}>
                  <div className="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2} width="17" height="17">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="info">
                    <span className="val">{entry.count}</span>
                    <span className="lbl">{entry.area}{i === 0 ? ' ⭐' : ''}</span>
                  </div>
                </SummaryCard>
              );
            })}

            <ViewToggle>
              <ToggleBtn $active={view === 'chart'} onClick={() => setView('chart')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width="14" height="14">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Chart
              </ToggleBtn>
              <ToggleBtn $active={view === 'table'} onClick={() => setView('table')}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width="14" height="14">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Table
              </ToggleBtn>
            </ViewToggle>
          </SummaryBar>

          {/* Tab Selector */}
          <TabSelector>
            <TabBtn $active={activeTab === 'dodo-nro-status'} onClick={() => setActiveTab('dodo-nro-status')}>
              📊 DODO NRO Status Breakdown
            </TabBtn>
            <TabBtn $active={activeTab === 'srno-wise'} onClick={() => setActiveTab('srno-wise')}>
              📋 Sr.No Wise Applications
            </TabBtn>
            <TabBtn $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
              📍 All Applications by Location
            </TabBtn>
          </TabSelector>

          {/* DODO NRO Status Tab Content */}
          {activeTab === 'dodo-nro-status' && (
            <>
              {/* Mini Stats */}
              <StatusStatsContainer>
                <StatusStatCard $type="inProcess" onClick={() => setModalFilter({ type: 'inProcess' })} title={`Applications with Intent Yes/Maybe/Not Defined (—),\nwith Application ID created, and any progress\n(file uploaded, name entered, profile picture, or Sr.No selected).\nExcludes Successful Paid and Intent "No" applications.\nClick to view.`}>
                  <div className="label">⏳ In Process (Qualified — Intent: Yes / Maybe / —)</div>
                  <div className="count">{statusReport.summary.totalInProcess}</div>
                </StatusStatCard>
                <StatusStatCard $type="applied" onClick={() => setModalFilter({ type: 'paid' })} title={`Applications that have completed payment (status: payment_complete).\nThese are fully submitted and paid DODO NRO applications.\nClick to view.`}>
                  <div className="label">✅ Successful Paid Applications</div>
                  <div className="count">{statusReport.summary.totalPaid}</div>
                </StatusStatCard>
                <StatusStatCard $type="total" onClick={() => setModalFilter({ type: 'total' })} title={`Total of In Process + Successful Paid.\nAll qualified DODO NRO applications combined.\nIn Process (${statusReport.summary.totalInProcess}) + Paid (${statusReport.summary.totalPaid}) = ${statusReport.summary.totalAll}\nClick to view all.`}>
                  <div className="label">📊 Total Qualified DODO NRO</div>
                  <div className="count">{statusReport.summary.totalAll}</div>
                </StatusStatCard>
              </StatusStatsContainer>

              {/* Status Breakdown Table */}
              <StatusTableContainer>
                <StatusTable>
                  <thead>
                    <tr>
                      <th>Location / Area</th>
                      <th style={{ textAlign: 'center', width: '20%' }}>In Process</th>
                      <th style={{ textAlign: 'center', width: '20%' }}>Successful Paid</th>
                      <th style={{ textAlign: 'center', width: '15%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusReport.areas.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 500 }}>{item.area}</td>
                        <td style={{ textAlign: 'center' }}>
                          <CountBadge $type="inProcess" $clickable onClick={() => setModalFilter({ type: 'inProcess', area: item.area })}>
                            {item.inProcessCount}
                          </CountBadge>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <CountBadge $type="applied" $clickable onClick={() => setModalFilter({ type: 'paid', area: item.area })}>
                            {item.paidCount}
                          </CountBadge>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>
                          {item.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StatusTable>
              </StatusTableContainer>

            </>
          )}

          {/* Sr.No Wise Tab Content */}
          {activeTab === 'srno-wise' && (
            <>
              <StatusStatsContainer>
                <StatusStatCard $type="total" title={`Total unique CNG Station Location Sr.No numbers\nthat have at least one qualified application.`}>
                  <div className="label">📋 Unique Sr.No Locations</div>
                  <div className="count">{srNoReport.length}</div>
                </StatusStatCard>
                <StatusStatCard $type="inProcess" title={`Sr.No locations with more than one application.\nThese are high-demand locations.`}>
                  <div className="label">🔥 Sr.No with Multiple Apps</div>
                  <div className="count">{srNoReport.filter(s => s.count > 1).length}</div>
                </StatusStatCard>
                <StatusStatCard $type="applied" title={`Total qualified applications across all Sr.No locations.\nIntent: Yes/Maybe/— only.`}>
                  <div className="label">📄 Total Applications</div>
                  <div className="count">{totalWithSrNo}</div>
                </StatusStatCard>
              </StatusStatsContainer>

              <StatusTableContainer>
                <StatusTable>
                  <thead>
                    <tr>
                      <th style={{ width: '8%' }}>Sr.No</th>
                      <th style={{ width: '10%' }}>Area</th>
                      <th>Locality / Stretch</th>
                      <th style={{ textAlign: 'center', width: '12%' }}>In Process</th>
                      <th style={{ textAlign: 'center', width: '10%' }}>Paid</th>
                      <th style={{ textAlign: 'center', width: '10%' }}>Total</th>
                      <th style={{ width: '8%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {srNoReport.map((item, idx) => {
                      const isExpanded = expandedSrNos.has(item.srNo);
                      return (
                        <React.Fragment key={item.srNo}>
                          <tr style={{ background: item.count > 1 ? '#fffbeb' : undefined }}>
                            <td style={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.9rem' }}>{item.srNo}</td>
                            <td>
                              <span style={{
                                padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600,
                                background: item.area === 'PCMC' ? '#fef3c7' : item.area === 'PMC' ? '#dbeafe' : '#f1f5f9',
                                color: item.area === 'PCMC' ? '#92400e' : item.area === 'PMC' ? '#1e40af' : '#64748b',
                              }}>{item.area}</span>
                            </td>
                            <td style={{ fontSize: '0.78rem', color: '#475569', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.locality}>
                              {item.locality || '-'}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <CountBadge $type="inProcess">{item.inProcess}</CountBadge>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <CountBadge $type="applied">{item.paid}</CountBadge>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem' }}>
                              {item.count}
                            </td>
                            <td>
                              <ExpandBtn
                                $expanded={isExpanded}
                                onClick={() => {
                                  setExpandedSrNos(prev => {
                                    const next = new Set(prev);
                                    if (next.has(item.srNo)) next.delete(item.srNo);
                                    else next.add(item.srNo);
                                    return next;
                                  });
                                }}
                              >
                                {isExpanded ? 'Hide' : 'View'}
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </ExpandBtn>
                            </td>
                          </tr>
                          {isExpanded && item.applications.map((app: any, j: number) => (
                            <SubRow key={app.applicationId} $index={j}>
                              <SubTd style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{app.applicationId}</SubTd>
                              <SubTd colSpan={2}>{app.fullName || app.email || '-'}</SubTd>
                              <SubTd>{app.mobileNumber || '-'}</SubTd>
                              <SubTd>
                                {app.intent ? (
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 12, fontSize: '0.68rem', fontWeight: 600,
                                    textTransform: 'capitalize',
                                    background: app.intent === 'yes' ? '#dcfce7' : app.intent === 'maybe' ? '#fef3c7' : '#f1f5f9',
                                    color: app.intent === 'yes' ? '#15803d' : app.intent === 'maybe' ? '#92400e' : '#64748b',
                                  }}>{app.intent}</span>
                                ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </SubTd>
                              <SubTd><StatusBadge $status={app.status}>{app.status}</StatusBadge></SubTd>
                              <SubTd style={{ fontSize: '0.72rem' }}>{app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</SubTd>
                            </SubRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </StatusTable>
              </StatusTableContainer>
            </>
          )}

          {/* All Applications Tab Content */}
          {activeTab === 'all' && (
          <ContentBody>
            {view === 'chart' ? (
              <ChartSection>
                {areaReport.map((entry, i) => {
                  const pct = (entry.count / maxCount) * 100;
                  const isWide = pct > 20;
                  return (
                    <BarRow key={entry.area} $index={i} onClick={() => setModalFilter({ type: 'area-all', area: entry.area })}>
                      <RankBadge $rank={i}>{i + 1}</RankBadge>
                      <BarLabel $isTop={i === 0} title={entry.area}>{entry.area}</BarLabel>
                      <BarTrack style={{ '--bar-width': `${Math.max(pct, 2)}%` } as React.CSSProperties}>
                        <BarFill $pct={pct} $rank={i}>
                          {isWide && <BarCount $isInside>{entry.count}</BarCount>}
                        </BarFill>
                        {!isWide && <BarCount $isInside={false} style={{ left: `calc(${Math.max(pct, 2)}% + 8px)` }}>{entry.count}</BarCount>}
                      </BarTrack>
                    </BarRow>
                  );
                })}
              </ChartSection>
            ) : (
              <div>
                <Table>
                  <Thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Area</Th>
                      <Th>Applications</Th>
                      <Th>% Share</Th>
                      <Th>Details</Th>
                    </tr>
                  </Thead>
                  <tbody>
                    {areaReport.map((entry, i) => {
                      const isExpanded = expandedAreas.has(entry.area);
                      return (
                        <React.Fragment key={entry.area}>
                          <Tr $index={i}>
                            <Td><RankBadge $rank={i}>{i + 1}</RankBadge></Td>
                            <Td style={{ fontWeight: i < 3 ? 700 : 500 }}>
                              {entry.area}
                              {i === 0 && <TopBadge>Highest</TopBadge>}
                            </Td>
                            <Td><AreaCountBadge $rank={i}>{entry.count}</AreaCountBadge></Td>
                            <Td>{((entry.count / totalWithSrNo) * 100).toFixed(1)}%</Td>
                            <Td>
                              <ExpandBtn $expanded={isExpanded} onClick={() => toggleExpand(entry.area)}>
                                {isExpanded ? 'Hide' : 'View'}
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </ExpandBtn>
                            </Td>
                          </Tr>
                          {isExpanded && entry.applications.map((app, j) => (
                            <SubRow key={app.applicationId} $index={j}>
                              <SubTd />
                              <SubTd>{app.fullName || app.email}</SubTd>
                              <SubTd style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{app.applicationId}</SubTd>
                              <SubTd>Sr.No: {app.srNo}</SubTd>
                              <SubTd><StatusBadge $status={app.status}>{app.status}</StatusBadge></SubTd>
                            </SubRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </ContentBody>
          )}

          {/* Detail Modal (shared across both tabs) */}
          {modalFilter && (
            <ModalOverlay onClick={() => setModalFilter(null)}>
              <ModalContainer onClick={e => e.stopPropagation()}>
                <ModalHeader $color={modalMeta.color}>
                  <ModalTitle $color={modalMeta.color}>
                    {modalMeta.emoji} {modalMeta.title}
                    <span style={{ fontSize: '0.78rem', fontWeight: 500, color: '#64748b', marginLeft: 8 }}>
                      ({modalApplications.length} applications)
                    </span>
                  </ModalTitle>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {modalApplications.length > 0 && (
                      <button
                        onClick={exportModalToXlsx}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 14px', border: '1px solid #10b981', borderRadius: 8,
                          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', color: '#047857',
                          cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.2s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4, #dcfce7)'; e.currentTarget.style.transform = 'scale(1)'; }}
                        title="Export this data to Excel"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} width="13" height="13">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export {modalApplications.length} XLSX
                      </button>
                    )}
                    <ModalCloseBtn onClick={() => setModalFilter(null)}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </ModalCloseBtn>
                  </div>
                </ModalHeader>
                <ModalBody>
                  {modalApplications.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                      No applications found for this filter.
                    </div>
                  ) : (
                    <ModalTable>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Area</th>
                          <th>Sr.No</th>
                          <th>Application ID</th>
                          <th>Applicant Name</th>
                          <th>Mobile</th>
                          {modalMeta.showIntent && <th>Intent</th>}
                          <th>Status</th>
                          <th>Applied On</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalApplications.map((app, idx) => (
                          <tr key={app.applicationId + idx}>
                            <td>{idx + 1}</td>
                            <td>{app.area}</td>
                            <td style={{ fontWeight: 600 }}>{app.srNo || '-'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{app.applicationId}</td>
                            <td>{app.fullName || (app as any).email?.split('@')[0]?.toUpperCase() || '-'}</td>
                            <td>{app.mobileNumber || '-'}</td>
                            {modalMeta.showIntent && (
                              <td>
                                {(app as any).intent ? (
                                  <span style={{
                                    padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                                    textTransform: 'capitalize',
                                    background: (app as any).intent === 'yes' ? '#dcfce7' : (app as any).intent === 'maybe' ? '#fef3c7' : (app as any).intent === 'no' ? '#fee2e2' : '#f1f5f9',
                                    color: (app as any).intent === 'yes' ? '#15803d' : (app as any).intent === 'maybe' ? '#92400e' : (app as any).intent === 'no' ? '#b91c1c' : '#64748b',
                                  }}>{(app as any).intent}</span>
                                ) : (
                                  <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>—</span>
                                )}
                              </td>
                            )}
                            <td><StatusBadge $status={app.status}>{app.status}</StatusBadge></td>
                            <td>{app.createdAt ? new Date(app.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </ModalTable>
                  )}
                </ModalBody>
                <ModalFooter>
                  <span>Showing {modalApplications.length} application{modalApplications.length !== 1 ? 's' : ''}</span>
                  <span style={{ color: modalMeta.color, fontWeight: 600 }}>
                    {modalFilter.type === 'inProcess' ? 'Draft apps with form progress' : modalFilter.area ? modalFilter.area : 'All Areas'}
                  </span>
                </ModalFooter>
              </ModalContainer>
            </ModalOverlay>
          )}

          <FooterBar>
            <FooterInfo>
              {activeTab === 'all'
                ? `${areaReport.length} areas · ${totalWithSrNo} applications (with Sr.No)`
                : activeTab === 'srno-wise'
                ? `${srNoReport.length} unique Sr.No locations · ${totalWithSrNo} applications`
                : `Total: ${statusReport.summary.totalAll} qualified DODO NRO applications across ${statusReport.areas.length} areas`}
            </FooterInfo>
            <FooterInfo style={{ color: '#7c3aed', fontWeight: 600 }}>
              {activeTab === 'all'
                ? `Top: ${areaReport[0]?.area} (${areaReport[0]?.count} applications)`
                : activeTab === 'srno-wise'
                ? `${srNoReport.filter(s => s.count > 1).length} locations with multiple applications`
                : `In Process: ${statusReport.summary.totalInProcess} · Paid: ${statusReport.summary.totalPaid}`}
            </FooterInfo>
          </FooterBar>
        </>
      )}
    </PageContainer>
  );
};

export default AreaWiseReportPage;
